import { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    Card,
    CardContent,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Divider,
    Autocomplete,
    Slider,
} from '@mui/material';
import { useDataProvider, useTranslate, Title } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useQuery } from '@tanstack/react-query';
import SchoolMap from './SchoolMap';
import { SchoolMapItem } from './statistics/types';
import { getSchoolLevels, type SchoolLevel } from './schoolTypes';

type FilterLevel = SchoolLevel | 'yhtenais';

const FILTER_CONFIG: {
    key: FilterLevel;
    label: string;
    color: string;
    letter: string;
}[] = [
    { key: 'ala', label: 'Alakoulu', color: '#4CAF50', letter: 'A' },
    { key: 'yla', label: 'Yläkoulu', color: '#2196F3', letter: 'Y' },
    { key: 'lukio', label: 'Lukio', color: '#FF9800', letter: 'L' },
    {
        key: 'ammatillinen',
        label: 'Ammatillinen',
        color: '#9C27B0',
        letter: 'AM',
    },
    { key: 'yhtenais', label: 'Yhtenäiskoulu', color: '#E91E63', letter: 'YK' },
];

const MarkerIcon = ({ color, letter }: { color: string; letter: string }) => (
    <svg
        width="18"
        height="26"
        viewBox="0 0 28 40"
        style={{ verticalAlign: 'middle', marginRight: 4 }}
    >
        <path
            d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.3 21.7 0 14 0z"
            fill={color}
            stroke="#fff"
            strokeWidth="1.5"
        />
        <text
            x="14"
            y="18"
            textAnchor="middle"
            fill="#fff"
            fontSize={letter.length > 1 ? 9 : 11}
            fontWeight="bold"
            fontFamily="Arial,sans-serif"
        >
            {letter}
        </text>
    </svg>
);

const SchoolMapPage = () => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const { canAccess } = useCanAccess({
        action: 'show',
        resource: 'schoolmap',
    });
    const [cityFilter, setCityFilter] = useState<string | null>(null);
    const [minActiveSubs, setMinActiveSubs] = useState(0);
    const [minComingSubs, setMinComingSubs] = useState(0);
    const [minMpassPct, setMinMpassPct] = useState(0);
    const [levelFilter, setLevelFilter] = useState<
        Record<FilterLevel, boolean>
    >({
        ala: true,
        yla: true,
        lukio: true,
        ammatillinen: true,
        yhtenais: true,
    });

    const { data, isLoading, error } = useQuery<SchoolMapItem[]>({
        queryKey: ['schoolmap'],
        queryFn: () =>
            (dataProvider as any)
                .getAny('/statistics/schoolmap', {})
                .then((res: any) => res.data ?? res),
        staleTime: Infinity,
    });

    const cityOptions = useMemo(() => {
        if (!data) return [];
        const cities = new Set<string>();
        data.forEach(s => {
            const city = s.kunta || s.visit_city || s.city;
            if (city) cities.add(city);
        });
        return Array.from(cities).sort();
    }, [data]);

    const maxActive = useMemo(() => {
        if (!data) return 0;
        return data.reduce(
            (m, s) => Math.max(m, Number(s.active_subscriptions || 0)),
            0
        );
    }, [data]);

    const maxComing = useMemo(() => {
        if (!data) return 0;
        return data.reduce(
            (m, s) => Math.max(m, Number(s.coming_subscriptions || 0)),
            0
        );
    }, [data]);

    const allLevelsActive = Object.values(levelFilter).every(v => v);

    const filteredSchools = data
        ? data.filter(school => {
              if (cityFilter) {
                  const city =
                      school.kunta || school.visit_city || school.city || '';
                  if (city !== cityFilter) return false;
              }
              if (
                  minActiveSubs > 0 &&
                  Number(school.active_subscriptions || 0) < minActiveSubs
              )
                  return false;
              if (
                  minComingSubs > 0 &&
                  Number(school.coming_subscriptions || 0) < minComingSubs
              )
                  return false;
              if (minMpassPct > 0) {
                  const total =
                      Number(school.teacher_count || 0) +
                      Number(school.student_count || 0) +
                      Number(school.pupil_count || 0);
                  const mpass =
                      Number(school.teacher_mpass_count || 0) +
                      Number(school.student_mpass_count || 0) +
                      Number(school.pupil_mpass_count || 0);
                  const pct = total > 0 ? (mpass / total) * 100 : 0;
                  if (pct < minMpassPct) return false;
              }
              if (!allLevelsActive && school.school_type_code) {
                  const levels = getSchoolLevels(school.school_type_code);
                  const isYhtenais = levels.length > 1;
                  if (isYhtenais) {
                      if (!levelFilter.yhtenais) return false;
                  } else if (
                      levels.length > 0 &&
                      !levels.some(l => levelFilter[l])
                  ) {
                      return false;
                  }
              }
              return true;
          })
        : [];

    if (!canAccess) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Ei käyttöoikeutta
            </Alert>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Karttadatan lataus epäonnistui
            </Alert>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex' }}>
            <Title title="Koulukartta" />
            {/* Filter sidebar */}
            <Card
                sx={{
                    width: 280,
                    flexShrink: 0,
                    overflow: 'auto',
                    borderRadius: 0,
                }}
            >
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Koulukartta
                    </Typography>
                    <Autocomplete
                        options={cityOptions}
                        value={cityFilter}
                        onChange={(_, value) => setCityFilter(value)}
                        renderInput={params => (
                            <TextField {...params} label="Kunta" size="small" />
                        )}
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Koulutyyppi
                    </Typography>
                    <FormGroup>
                        {FILTER_CONFIG.map(({ key, label, color, letter }) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={levelFilter[key]}
                                        onChange={e =>
                                            setLevelFilter(f => ({
                                                ...f,
                                                [key]: e.target.checked,
                                            }))
                                        }
                                    />
                                }
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <MarkerIcon
                                            color={color}
                                            letter={letter}
                                        />
                                        {label}
                                    </Box>
                                }
                            />
                        ))}
                    </FormGroup>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Aktiiviset tilaukset ≥ {minActiveSubs}
                    </Typography>
                    <Slider
                        value={minActiveSubs}
                        onChange={(_, v) => setMinActiveSubs(v as number)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={maxActive || 1}
                        size="small"
                        sx={{ mx: 1, width: 'calc(100% - 16px)' }}
                    />
                    <Typography variant="subtitle2" gutterBottom>
                        Tulevat tilaukset ≥ {minComingSubs}
                    </Typography>
                    <Slider
                        value={minComingSubs}
                        onChange={(_, v) => setMinComingSubs(v as number)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={maxComing || 1}
                        size="small"
                        sx={{ mx: 1, width: 'calc(100% - 16px)' }}
                    />
                    <Typography variant="subtitle2" gutterBottom>
                        MPASSid käyttöaste ≥ {minMpassPct}%
                    </Typography>
                    <Slider
                        value={minMpassPct}
                        onChange={(_, v) => setMinMpassPct(v as number)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={v => `${v}%`}
                        min={0}
                        max={100}
                        size="small"
                        sx={{ mx: 1, width: 'calc(100% - 16px)' }}
                    />
                    <Divider sx={{ my: 1 }} />
                    <Box
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h5" fontWeight="bold">
                            {isLoading ? '...' : filteredSchools.length}
                        </Typography>
                        <Typography variant="caption">
                            koulua kartalla
                        </Typography>
                    </Box>
                    {!isLoading &&
                        filteredSchools.length > 0 &&
                        (() => {
                            const totals = filteredSchools.reduce(
                                (acc, s) => {
                                    acc.t += Number(s.teacher_count || 0);
                                    acc.tm += Number(
                                        s.teacher_mpass_count || 0
                                    );
                                    acc.s += Number(s.student_count || 0);
                                    acc.sm += Number(
                                        s.student_mpass_count || 0
                                    );
                                    acc.p += Number(s.pupil_count || 0);
                                    acc.pm += Number(s.pupil_mpass_count || 0);
                                    return acc;
                                },
                                { t: 0, tm: 0, s: 0, sm: 0, p: 0, pm: 0 }
                            );
                            const pct = (part: number, total: number) =>
                                total > 0
                                    ? `${Math.round((part / total) * 100)}%`
                                    : '0%';
                            const all = totals.t + totals.s + totals.p;
                            const allM = totals.tm + totals.sm + totals.pm;
                            const Row = ({
                                label,
                                mpass,
                                total: tot,
                            }: {
                                label: string;
                                mpass: number;
                                total: number;
                            }) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        py: 0.25,
                                    }}
                                >
                                    <Typography variant="body2">
                                        {label}{' '}
                                        <strong>{pct(mpass, tot)}</strong>
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {mpass}/{tot}
                                    </Typography>
                                </Box>
                            );
                            return (
                                <Box sx={{ mt: 1 }}>
                                    <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                    >
                                        MPASSid käyttöaste
                                    </Typography>
                                    <Row
                                        label="Opettajat"
                                        mpass={totals.tm}
                                        total={totals.t}
                                    />
                                    <Row
                                        label="Opiskelijat"
                                        mpass={totals.sm}
                                        total={totals.s}
                                    />
                                    <Row
                                        label="Oppilaat"
                                        mpass={totals.pm}
                                        total={totals.p}
                                    />
                                    <Divider sx={{ my: 0.5 }} />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                        >
                                            Yhteensä{' '}
                                            <strong>{pct(allM, all)}</strong>
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight="bold"
                                        >
                                            {allM}/{all}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })()}
                </CardContent>
            </Card>

            {/* Map */}
            <Box sx={{ flex: 1, position: 'relative' }}>
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <SchoolMap schools={filteredSchools} height="100%" />
                )}
            </Box>
        </Box>
    );
};

export default SchoolMapPage;
